import { HStack, Menu, Portal } from '@chakra-ui/react';
import * as React from 'react';
import { useMediaQuery } from 'react-responsive';
import styled from 'styled-components';
import {
  IReportOption,
  ReportId,
  reportOptions,
} from '../../data/providers/reports/reportOptions';
import { EntityType } from '../../data/types';
import { KEYS } from '../../keys';
import { Icon } from '../shared/Icon';
import { ISegmentItem, SegmentGroup } from '../shared/SegmentGroup';

interface IProps {
  onReportSelected: (report: IReportOption) => void;
  selected: IReportOption;
  entityType: EntityType;
}

const reportIcons: Record<ReportId, string> = {
  trend: 'trending_up',
  profile: 'person',
  comparison: 'compare_arrows',
  ratio: 'percent',
  asymmetry: 'balance',
  dsi: 'fitness_center',
  eur: 'speed',
  block: 'calendar_month',
  weight: 'scale',
  quadrant: 'grid_view',
  tsa: 'sports_score',
};

export const ReportMenu = (props: IProps): React.ReactElement => {
  const useDropdown = useMediaQuery({ maxWidth: 780 });

  const selectedReport =
    props.selected ??
    reportOptions.find((r: IReportOption): boolean => r.id === 'trend');

  /**
   * @description filters multi athlete reports out if viewing a single athlete
   */
  const options = React.useMemo((): IReportOption[] => {
    return reportOptions.filter((report: IReportOption): boolean => {
      if (report.multiAthlete && props.entityType === 'athletes') {
        return false;
      } else {
        return true;
      }
    });
  }, [props.entityType]);

  const items = React.useMemo((): ISegmentItem[] => {
    return options.map(
      (option: IReportOption): ISegmentItem => ({
        value: option.id,
        label: option.name.replace(' Report', ''),
        'data-testid':
          option.id === 'asymmetry'
            ? KEYS.ASYMMETRY_REPORT_MENU_ITEM
            : undefined,
      })
    );
  }, [options]);

  /**
   * @description handles the user selecting a report
   */
  const handleSelect = (details: { value: string }): void => {
    const report = reportOptions.find(
      (r: IReportOption): boolean => details.value === r.id
    );
    if (report.id === props.selected.id) return;
    props.onReportSelected(report);
  };

  if (useDropdown) {
    return (
      <Menu.Root
        onSelect={handleSelect}
        positioning={{ placement: 'bottom-start' }}
      >
        <Menu.Trigger
          display='flex'
          alignItems='center'
          justifyContent='space-between'
          gap='8px'
          bg='menuBackground'
          border='1px solid'
          borderColor='lightText'
          borderRadius='6px'
          color='menuText'
          cursor='pointer'
          padding='6px 10px'
          width='calc(100% - 16px)'
          margin='8px'
          fontSize='sm'
        >
          <span>{selectedReport.name.replace(' Report', '')}</span>
          <Icon name='expand_more' size='small' />
        </Menu.Trigger>
        <Portal>
          <Menu.Positioner>
            <Menu.Content
              bg='menuBackground'
              border='1px solid'
              borderColor='lightText'
              borderRadius='8px'
              padding='0.5rem'
              minWidth='200px'
              css={{
                '& [data-part="item"]': {
                  borderRadius: '4px',
                  padding: '6px 10px',
                  cursor: 'pointer',
                  _hover: {
                    background: 'menuHoverBackground',
                  },
                },
              }}
            >
              {options.map(
                (option: IReportOption): React.ReactElement => (
                  <Menu.Item
                    key={option.id}
                    value={option.id}
                    data-testid={
                      option.id === 'asymmetry'
                        ? KEYS.ASYMMETRY_REPORT_MENU_ITEM
                        : undefined
                    }
                  >
                    {option.name.replace(' Report', '')}
                  </Menu.Item>
                )
              )}
            </Menu.Content>
          </Menu.Positioner>
        </Portal>
      </Menu.Root>
    );
  }

  return (
    <ReportMenuWrapper>
      <SegmentGroup
        value={selectedReport?.id}
        items={items}
        onValueChange={handleSelect}
        variant='fullWidth'
        size='md'
        style={{ padding: '8px' }}
      />
    </ReportMenuWrapper>
  );
};

const ReportMenuWrapper = styled.div`
  [data-part='item-text'] {
    font-size: 16px;
  }
`;
